import axios from 'axios';
// Remove the conflicting import and use the interfaces defined in this file
// import { FoodItem, HungerZone } from '../components/land/FoodTracker';

// Define base API URL - replace with your actual backend API endpoint
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ecofusion-backend.vercel.app';

// Create axios instance with common configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 seconds
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false // Explicitly disable credentials for CORS
});

// Add request interceptor for debugging
apiClient.interceptors.request.use((request) => {
  console.log('Starting API Request:', request.method?.toUpperCase(), (request.baseURL || '') + (request.url || ''));
  return request;
}, (error) => {
  console.error('Request error:', error);
  return Promise.reject(error);
});

// Add response interceptor for debugging
apiClient.interceptors.response.use(response => {
  console.log('Response received:', response.status, response.config.url);
  return response;
}, error => {
  console.error('Response error:', error.message);
  if (error.response) {
    console.error('Status:', error.response.status);
    console.error('Data:', error.response.data);
  }
  return Promise.reject(error);
});

// API Food Item definition
export interface FoodItem {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  expiryDate: string;
  shelfLife: number; // in days
  location: string;
  storageCondition: string;
}

// Legacy FoodItem interface for backwards compatibility
export interface LegacyFoodItem {
  id: string;
  name: string;
  quantity: number;
  country: string;
  state: string;
  city: string;
  expirationDate: string;
}

// API Service for Food Module

// Food Item Interface
export interface FoodNeed {
  type: string;
  quantityNeeded: number;
  unit: string;
}

export interface HungerZone {
  id: string;
  region: string;
  severity: number;
  affectedPopulation: number;
  latitude: number;
  longitude: number;
  foodNeeds: FoodNeed[];
  distance?: number; // Optional field calculated during runtime
}

// Food Inventory API
export const getFoodInventory = async (): Promise<FoodItem[]> => {
  try {
    const response = await apiClient.get('/api/food/inventory');
    return response.data;
  } catch (error) {
    console.error('Error fetching food inventory:', error);
    // Return empty array on error, but real applications should handle errors properly
    return [];
  }
};

// Add food item to inventory
export const addFoodItem = async (foodItem: Omit<FoodItem, 'id'>): Promise<FoodItem> => {
  try {
    const response = await apiClient.post('/api/food/inventory', foodItem);
    return response.data;
  } catch (error) {
    console.error('Error adding food item:', error);
    throw error;
  }
};

// Get all hunger zones
export const getHungerZones = async (): Promise<HungerZone[]> => {
  try {
    const response = await apiClient.get('/api/food/hunger-zones');
    return response.data;
  } catch (error) {
    console.error('Error fetching hunger zones:', error);
    return [];
  }
};

// Get hunger zones nearest to a location
export const getNearestHungerZones = async (latitude: number, longitude: number, limit: number = 10): Promise<HungerZone[]> => {
  try {
    const response = await apiClient.get('/api/food/hunger-zones/nearest', {
      params: { latitude, longitude, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching nearest hunger zones:', error);
    return [];
  }
};

// Transfer food to a hunger zone
export interface FoodTransferRequest {
  hungerZoneId: string;
  foodItems: Array<{
    foodItemId: string;
    quantity: number;
  }>;
  transportInfo?: {
    estimatedArrival: string;
    transportMethod: string;
    contactPerson?: string;
  };
}

export interface FoodTransferResponse {
  success: boolean;
  message: string;
  transferId?: string;
  estimatedArrival?: string;
}

export const transferFood = async (request: FoodTransferRequest): Promise<FoodTransferResponse> => {
  try {
    const response = await apiClient.post('/api/food/transfer', request);
    return response.data;
  } catch (error) {
    console.error('Error transferring food:', error);
    throw error;
  }
};

// Get shelf life prediction for a food item
export const getShelfLifePrediction = async (foodName: string, storageCondition: string): Promise<number | null> => {
  try {
    const response = await apiClient.get('/api/food/shelf-life-prediction', {
      params: { foodName, storageCondition }
    });
    return response.data.shelfLife;
  } catch (error) {
    console.error('Error predicting shelf life:', error);
    return null;
  }
};

// Get live data updates about hunger zones and food inventory
export interface LiveDataUpdate {
  timestamp: string;
  hungerZones: HungerZone[];
  inventory?: FoodItem[];
  alerts?: {
    severity: 'info' | 'warning' | 'error';
    message: string;
    zoneId?: string;
  }[];
}

export const getLiveDataUpdates = async (latitude: number, longitude: number): Promise<LiveDataUpdate> => {
  try {
    const response = await apiClient.get('/api/food/live-data', {
      params: { latitude, longitude }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching live data updates:', error);
    throw error;
  }
};

// Fetch food data with optional state and city filters
export const fetchFoodData = async (country: string, state: string, city: string): Promise<LegacyFoodItem[]> => {
  try {
    const response = await apiClient.get('/api/food', {
      params: { country, state, city },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching food data:', error);
    return [];
  }
};

/**
 * Uploads a soil image for analysis
 * @param file The image file to upload
 * @returns The analysis result
 */
export const uploadSoilImage = async (file: File): Promise<any> => {
  try {
    console.log('Preparing to upload soil image...');
    const formData = new FormData();
    formData.append('image', file);

    // Upload the image
    const response = await apiClient.post('/api/soil/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 60000, // 1 minute timeout for upload
    });

    console.log('Upload response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error uploading soil image:', error);

    // Format and rethrow the error
    let message = 'Failed to upload soil image';
    if (error.response && error.response.data && error.response.data.message) {
      message = error.response.data.message;
    } else if (error.message) {
      message = error.message;
    }

    throw new Error(message);
  }
};

/**
 * Analyzes a previously uploaded soil image
 * @param imageId The ID of the uploaded image to analyze
 * @returns The analysis result
 */
export const analyzeSoilImage = async (imageId: string): Promise<any> => {
  try {
    console.log('Preparing to analyze soil image...');

    // Analyze the image
    const response = await apiClient.post(`/api/soil/${imageId}/analyze`, {}, {
      timeout: 180000, // 3 minutes timeout for analysis
    });

    console.log('Analysis response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error analyzing soil image:', error);

    // Format and rethrow the error
    let message = 'Failed to analyze soil image';
    if (error.response && error.response.data && error.response.data.message) {
      message = error.response.data.message;
    } else if (error.message) {
      message = error.message;
    }

    throw new Error(message);
  }
};

// Direct OpenAI API call for soil analysis as fallback for CORS errors
export const directOpenAISoilAnalysis = async (file: File): Promise<any> => {
  try {
    console.log('Using direct EcoFusion AI fallback for soil analysis');

    // Convert file to base64
    const base64Image = await fileToBase64(file);
    const mimeType = file.type || 'image/jpeg';

    // First check for environment variable
    let openaiApiKey = process.env.REACT_APP_OPENAI_API_KEY || '';
    console.log('EcoFusion AI API key from environment:', openaiApiKey ? 'Found (not showing full key for security)' : 'Not found');

    // If not found in environment, check localStorage
    if (!openaiApiKey) {
      console.log('EcoFusion AI API key not found in environment, checking localStorage...');
      const storedKey = localStorage.getItem('openai_api_key');
      if (storedKey) {
        console.log('EcoFusion AI API key found in localStorage');
        openaiApiKey = storedKey;
      } else {
        console.log('EcoFusion AI API key not found in localStorage');
      }
    }

    // Only prompt if no key is found in either place
    if (!openaiApiKey) {
      console.log('No EcoFusion AI API key found, prompting user...');
      // Inform user that we're using direct EcoFusion AI API
      const promptedKey = prompt('Please enter your EcoFusion AI API key for direct analysis. This is only needed when the backend server is unavailable:');

      if (!promptedKey) {
        console.log('User cancelled EcoFusion AI API key prompt');
        throw new Error('EcoFusion AI API key is required for direct analysis');
      }

      console.log('User provided an EcoFusion AI API key');
      openaiApiKey = promptedKey;
      // Store the API key for future use
      localStorage.setItem('openai_api_key', openaiApiKey);
    }

    if (!openaiApiKey) {
      throw new Error('Could not obtain EcoFusion AI API key from any source');
    }

    console.log('Using EcoFusion AI API for direct analysis with valid API key');

    // Call OpenAI API directly
    const response = await axios.post(
      'https://api.openai.com/v1/chat/completions',
      {
        model: 'gpt-4o',
        messages: [
          {
            role: 'user',
            content: [
              {
                type: 'text',
                text: "Analyze this soil image and tell me if it contains soil. If it does, provide details about the soil type, fertility, pH level, and what crops might grow well in it. Structure your response as a JSON object with fields: isSoil (boolean), soilType, estimatedFertility, pH (number), and recommendedCrops (array)."
              },
              {
                type: 'image_url',
                image_url: {
                  url: `data:${mimeType};base64,${base64Image}`
                }
              }
            ]
          }
        ],
        max_tokens: 800
      },
      {
        headers: {
          'Authorization': `Bearer ${openaiApiKey}`,
          'Content-Type': 'application/json'
        },
        timeout: 120000 // 120 seconds timeout (2 minutes)
      }
    );

    // Extract the text response
    const analysisText = response.data.choices[0].message.content;
    console.log('Direct OpenAI analysis:', analysisText);

    // Try to parse as JSON
    try {
      // Extract JSON if it's embedded in the response
      const jsonMatch = analysisText.match(/```json\n([\s\S]*)\n```/) ||
        analysisText.match(/{[\s\S]*}/);

      const jsonStr = jsonMatch ? jsonMatch[1] || jsonMatch[0] : analysisText;
      const analysisData = JSON.parse(jsonStr);

      // Create a response object similar to the backend's response
      return {
        _id: `direct-openai-${Date.now()}`,
        imageUrl: URL.createObjectURL(file),
        isSoil: analysisData.isSoil || true,
        soilType: analysisData.soilType || 'Unknown',
        estimatedFertility: analysisData.estimatedFertility || 'Good',
        pH: analysisData.pH || 7.0,
        recommendedCrops: analysisData.recommendedCrops || [],
        analysisResults: {
          nitrogen: analysisData.nitrogen || 'Medium',
          phosphorus: analysisData.phosphorus || 'Medium',
          potassium: analysisData.potassium || 'Medium',
          moisture: analysisData.moisture || 'Medium'
        },
        createdAt: new Date().toISOString(),
        analysisMethod: 'Direct OpenAI Analysis',
        directOpenAIResponse: analysisText
      };
    } catch (parseError) {
      console.error('Failed to parse OpenAI response as JSON:', parseError);

      // Return a structured response anyway with the raw text
      return {
        _id: `direct-openai-${Date.now()}`,
        imageUrl: URL.createObjectURL(file),
        isSoil: true,
        soilType: 'Unknown',
        estimatedFertility: 'Unknown',
        pH: 7.0,
        recommendedCrops: [],
        createdAt: new Date().toISOString(),
        analysisMethod: 'Direct OpenAI Analysis',
        directOpenAIResponse: analysisText
      };
    }
  } catch (error) {
    console.error('Error in direct OpenAI analysis:', error);
    throw error;
  }
};

// Helper function to convert File to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = reader.result as string;
      // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = base64String.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
};

// Submit parameters to receive a personalized diet plan recommendation
export const fetchDietPlan = async (bmi: string, userData?: {
  gender?: string;
  age?: string;
  height?: string;
  weight?: string;
  medicalReport?: File;
}): Promise<string> => {
  try {
    // If we have full user data, use FormData for the medical report upload
    if (userData) {
      const formData = new FormData();
      formData.append('bmi', bmi);

      if (userData.gender) formData.append('gender', userData.gender);
      if (userData.age) formData.append('age', userData.age);
      if (userData.height) formData.append('height', userData.height);
      if (userData.weight) formData.append('weight', userData.weight);

      if (userData.medicalReport) {
        formData.append('medicalReport', userData.medicalReport);
      }

      const response = await apiClient.post('/api/diet/enhanced', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data.dietPlan;
    } else {
      // Use the original endpoint for backward compatibility
      const response = await apiClient.post('/api/diet', { bmi });
      return response.data.dietPlan;
    }
  } catch (error) {
    console.error('Error fetching diet plan:', error);
    return 'Error fetching diet plan';
  }
};

/* ------------------------------------------------------------------
   WATER ENDPOINTS
   ------------------------------------------------------------------ */
export interface WaterData {
  id?: string;
  region: string;
  ppm: number;
  arsenic: number;
  dissolvedOxygen: number;
  turbidity: number;
  temperature: number;
  pH: number;
  conductivity: number;
  salinity: number;
  tds: number;
  hardness: number;
  acidity: number;
  cdom: number;
}

// Fetch water data (optional region filter)
export const fetchWaterData = async (region?: string): Promise<WaterData[]> => {
  try {
    const response = await apiClient.get('/api/water', {
      params: { region },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching water data:', error);
    return [];
  }
};

// Create (ingest) new water data
export const createWaterData = async (data: WaterData): Promise<WaterData> => {
  try {
    const response = await apiClient.post('/api/water', data);
    return response.data;
  } catch (error) {
    console.error('Error creating water data:', error);
    throw error;
  }
};

// Interface for water quality by location response
export interface WaterQualityByLocationResponse {
  currentLocation: {
    region: string;
    fullLocation?: {
      city: string | null;
      state: string | null;
      country: string | null;
      formatted: string;
      latitude: number;
      longitude: number;
    };
    data: WaterData | null;
    qualityScore: number | null;
  };
  nearestBetterQuality: WaterData | null;
  betterQualityLocations: WaterData[];
  waterTips: string[];
}

// Get water quality data based on geolocation
export const getWaterQualityByLocation = async (
  latitude: number,
  longitude: number
): Promise<WaterQualityByLocationResponse> => {
  try {
    const response = await apiClient.get('/api/water/location', {
      params: { latitude, longitude }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting water quality by location:', error);
    throw error;
  }
};

// Interface for heatmap data
export interface WaterQualityHeatmapPoint {
  id: string;
  region: string;
  latitude: number;
  longitude: number;
  qualityScore: number;
  ppm: number;
  pH: number;
  arsenic: number;
  turbidity: number;
  temperature: number;
  dissolvedOxygen: number;
  hardness: number;
  distance?: number; // Optional, calculated on the client
}

// Get water quality data for heat map visualization
export const getWaterQualityHeatmap = async (): Promise<WaterQualityHeatmapPoint[]> => {
  try {
    const response = await apiClient.get('/api/water/quality/heatmap');
    return response.data;
  } catch (error) {
    console.error('Error fetching water quality heatmap data:', error);

    // Mock data for development
    return [
      {
        id: '1',
        region: 'Bangalore Central',
        latitude: 12.9716,
        longitude: 77.5946,
        qualityScore: 75,
        ppm: 180,
        pH: 7.2,
        arsenic: 0.0001,
        turbidity: 5,
        temperature: 25,
        dissolvedOxygen: 8,
        hardness: 120
      },
      // ... other mock data points
    ];
  }
};

/* ------------------------------------------------------------------
   ENERGY ENDPOINTS
   ------------------------------------------------------------------ */
export interface EnergyPotentialPoint {
  id: string;
  region: string;
  country: string;
  state: string;
  latitude: number;
  longitude: number;
  solar: {
    potential: number; // 0-100 scale
    annualOutput: number; // kWh per kW installed
    installationFeasibility: number; // 0-100 scale
  };
  wind: {
    potential: number; // 0-100 scale
    averageSpeed: number; // m/s
    annualOutput: number; // kWh per turbine
    installationFeasibility: number; // 0-100 scale
  };
  hydro: {
    potential: number; // 0-100 scale
    annualOutput: number; // kWh
    installationFeasibility: number; // 0-100 scale
  };
  geothermal: {
    potential: number; // 0-100 scale
    temperature: number; // °C
    depth: number; // meters
    installationFeasibility: number; // 0-100 scale
  };
  biomass: {
    potential: number; // 0-100 scale
    availableFeedstock: number; // tons per year
    installationFeasibility: number; // 0-100 scale
  };
  recommendations: {
    primarySource: string;
    secondarySource: string;
    reasonPrimary: string;
    reasonSecondary: string;
  };
}

export interface EnergyCostData {
  country: string;
  state: string;
  electricityPrice: number; // per kWh
  averageConsumption: number; // kWh per household/year
  solarInstallationCost: number; // per kW
  windInstallationCost: number; // per turbine
  hydroInstallationCost: number; // for small scale
  geothermalInstallationCost: number; // for small scale
  biomassInstallationCost: number; // for small scale
  governmentIncentives: {
    type: string;
    description: string;
    value: number; // percentage or fixed amount
  }[];
  carbonCredit: number; // price per ton of CO2
}

export interface EnergySetupCostRequest {
  latitude: number;
  longitude: number;
  energyType: 'solar' | 'wind' | 'hydro' | 'geothermal' | 'biomass';
  capacity: number; // kW
  investmentBudget?: number; // optional
  landArea?: number; // square meters, optional
}

export interface EnergySetupCostResponse {
  initialCost: number;
  annualProduction: number; // kWh
  annualSavings: number;
  paybackPeriod: number; // years
  roi: number; // percentage
  co2Reduction: number; // tons per year
  carbonCreditValue: number; // monetary value
  governmentIncentives: {
    name: string;
    value: number;
  }[];
  totalIncentives: number;
  netCost: number; // after incentives
  maintenanceCost: number; // annual
  lifespanYears: number;
  breakdown: {
    equipmentCost: number;
    installationCost: number;
    permitsCost: number;
    landCost?: number;
  };
  longTermProfit: {
    year5: number;
    year10: number;
    year15: number;
    year20: number;
    year25: number;
  };
  recommendations: string[];
}

// Get energy potential heatmap data
export const getEnergyPotentialHeatmap = async (): Promise<EnergyPotentialPoint[]> => {
  try {
    const response = await apiClient.get('/api/energy/potential/heatmap');
    return response.data;
  } catch (error) {
    console.error('Error fetching energy potential heatmap data:', error);
    // Returns mock data
    return [];
  }
};

// Get nearest energy potential point based on location
export const getNearestEnergyPotential = async (
  latitude: number,
  longitude: number
): Promise<EnergyPotentialPoint> => {
  try {
    const response = await apiClient.get('/api/energy/potential/nearest', {
      params: { latitude, longitude },
      timeout: 5000 // Shorter timeout to fail faster
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching nearest energy potential data:', error);

    // Instead of throwing, provide fallback data based on the provided coordinates
    return generateFallbackEnergyPotential(latitude, longitude);
  }
};

// Generate fallback energy potential data when API fails
const generateFallbackEnergyPotential = (latitude: number, longitude: number): EnergyPotentialPoint => {
  // Determine basic region info based on coordinates
  let region = 'Unknown Region';
  let country = 'Unknown Country';
  let state = 'Unknown State';

  // Very basic country detection based on latitude/longitude ranges
  // This is just a fallback and not meant to be geographically accurate
  if (latitude > 24 && latitude < 50 && longitude > -125 && longitude < -66) {
    country = 'United States';
    state = 'California';
    region = 'West Coast';
  } else if (latitude > 35 && latitude < 60 && longitude > -10 && longitude < 30) {
    country = 'Europe';
    state = 'Central Europe';
    region = 'European Region';
  } else if (latitude > 8 && latitude < 37 && longitude > 68 && longitude < 97) {
    country = 'India';
    state = 'Maharashtra';
    region = 'Western India';
  }

  // Generate random but reasonable potential values
  const solarPotential = Math.floor(60 + Math.random() * 35); // 60-95%
  const windPotential = Math.floor(40 + Math.random() * 50); // 40-90%
  const hydroPotential = Math.floor(30 + Math.random() * 50); // 30-80%
  const geothermalPotential = Math.floor(20 + Math.random() * 70); // 20-90%
  const biomassPotential = Math.floor(30 + Math.random() * 60); // 30-90%

  // Determine primary and secondary sources based on generated potentials
  const potentials = [
    { type: 'solar', value: solarPotential },
    { type: 'wind', value: windPotential },
    { type: 'hydro', value: hydroPotential },
    { type: 'geothermal', value: geothermalPotential },
    { type: 'biomass', value: biomassPotential }
  ];

  potentials.sort((a, b) => b.value - a.value);
  const primarySource = potentials[0].type;
  const secondarySource = potentials[1].type;

  return {
    id: `fallback-${Date.now()}`,
    region,
    country,
    state,
    latitude,
    longitude,
    solar: {
      potential: solarPotential,
      annualOutput: Math.floor(1200 + Math.random() * 700), // 1200-1900 kWh
      installationFeasibility: Math.floor(70 + Math.random() * 25) // 70-95%
    },
    wind: {
      potential: windPotential,
      averageSpeed: 3 + Math.random() * 8, // 3-11 m/s
      annualOutput: Math.floor(7000 + Math.random() * 8000), // 7000-15000 kWh
      installationFeasibility: Math.floor(60 + Math.random() * 35) // 60-95%
    },
    hydro: {
      potential: hydroPotential,
      annualOutput: Math.floor(1000 + Math.random() * 4000), // 1000-5000 kWh
      installationFeasibility: Math.floor(40 + Math.random() * 50) // 40-90%
    },
    geothermal: {
      potential: geothermalPotential,
      temperature: Math.floor(80 + Math.random() * 200), // 80-280°C
      depth: Math.floor(1000 + Math.random() * 3000), // 1000-4000m
      installationFeasibility: Math.floor(50 + Math.random() * 40) // 50-90%
    },
    biomass: {
      potential: biomassPotential,
      availableFeedstock: Math.floor(30000 + Math.random() * 100000), // 30000-130000 tons
      installationFeasibility: Math.floor(55 + Math.random() * 40) // 55-95%
    },
    recommendations: {
      primarySource,
      secondarySource,
      reasonPrimary: `High ${primarySource} potential in this region`,
      reasonSecondary: `Good ${secondarySource} conditions as an alternative energy source`
    }
  };
};

// Get energy cost data by location (country and optionally state)
export const getEnergyCostData = async (
  country: string,
  state?: string
): Promise<EnergyCostData> => {
  try {
    const response = await apiClient.get('/api/energy/costs', {
      params: { country, state }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching energy cost data:', error);
    // Mock data response
    return {
      country: country,
      state: state || 'N/A',
      electricityPrice: 0.15,
      averageConsumption: 10000,
      solarInstallationCost: 1500,
      windInstallationCost: 200000,
      hydroInstallationCost: 100000,
      geothermalInstallationCost: 250000,
      biomassInstallationCost: 180000,
      governmentIncentives: [
        {
          type: 'Tax Credit',
          description: 'Federal tax credit for renewable energy',
          value: 30
        }
      ],
      carbonCredit: 25
    };
  }
};

// Calculate energy setup costs and ROI
export const calculateEnergySetupCost = async (
  request: EnergySetupCostRequest
): Promise<EnergySetupCostResponse> => {
  try {
    const response = await apiClient.post('/api/energy/calculator', request);
    return response.data;
  } catch (error) {
    console.error('Error calculating energy setup costs:', error);
    // Create simulated response
    return {
      initialCost: 0,
      annualProduction: 0,
      annualSavings: 0,
      paybackPeriod: 0,
      roi: 0,
      co2Reduction: 0,
      carbonCreditValue: 0,
      governmentIncentives: [],
      totalIncentives: 0,
      netCost: 0,
      maintenanceCost: 0,
      lifespanYears: 0,
      breakdown: {
        equipmentCost: 0,
        installationCost: 0,
        permitsCost: 0
      },
      longTermProfit: {
        year5: 0,
        year10: 0,
        year15: 0,
        year20: 0,
        year25: 0
      },
      recommendations: []
    };
  }
};

// Function to get location information from coordinates using OpenCage API
export interface LocationInfo {
  city: string | null;
  state: string | null;
  country: string | null;
  formatted: string;
  latitude: number;
  longitude: number;
}

// Helper function to add delay between retries
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Enhanced getLocationFromCoordinates with retry logic and multiple fallback options
export const getLocationFromCoordinates = async (latitude: number, longitude: number): Promise<LocationInfo> => {
  // Try OpenCage API first (with retries)
  try {
    // Try up to 3 times with OpenCage
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await fetch(`https://api.opencagedata.com/geocode/v1/json?q=${latitude}+${longitude}&key=${process.env.REACT_APP_OPENCAGE_API_KEY}&language=en`);

        if (!response.ok) {
          throw new Error(`OpenCage API returned ${response.status}`);
        }

        const data = await response.json();

        if (data.results && data.results.length > 0) {
          const result = data.results[0];
          const components = result.components;

          return {
            city: components.city || components.town || components.village || null,
            state: components.state || components.county || null,
            country: components.country || null,
            formatted: result.formatted || '',
            latitude,
            longitude
          };
        }

        // If no results, wait and retry
        if (attempt < 2) {
          await delay(1000); // Wait 1 second before retrying
          console.log(`OpenCage retry attempt ${attempt + 2}...`);
        }
      } catch (error) {
        if (attempt < 2) {
          await delay(1000);
          console.log(`OpenCage retry attempt ${attempt + 2} after error...`);
        } else {
          throw error; // Rethrow on final attempt
        }
      }
    }

    // If we get here, OpenCage failed all retries
    throw new Error('OpenCage API failed after multiple attempts');
  } catch (opencageError) {
    console.error('OpenCage API failed:', opencageError);

    // FALLBACK 1: Try Nominatim (OpenStreetMap) API
    try {
      console.log('Trying Nominatim fallback...');
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`);

      if (!response.ok) {
        throw new Error(`Nominatim API returned ${response.status}`);
      }

      const data = await response.json();

      if (data && data.address) {
        return {
          city: data.address.city || data.address.town || data.address.village || null,
          state: data.address.state || data.address.county || null,
          country: data.address.country || null,
          formatted: data.display_name || '',
          latitude,
          longitude
        };
      }
      throw new Error('No results from Nominatim');
    } catch (nominatimError) {
      console.error('Nominatim fallback failed:', nominatimError);

      // FALLBACK 2: Try BigDataCloud Reverse Geocoding API (free tier)
      try {
        console.log('Trying BigDataCloud fallback...');
        const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);

        if (!response.ok) {
          throw new Error(`BigDataCloud API returned ${response.status}`);
        }

        const data = await response.json();

        if (data) {
          return {
            city: data.city || data.locality || null,
            state: data.principalSubdivision || data.locality || null,
            country: data.countryName || null,
            formatted: `${data.city || data.locality || ''}, ${data.principalSubdivision || ''}, ${data.countryName || ''}`,
            latitude,
            longitude
          };
        }
        throw new Error('No results from BigDataCloud');
      } catch (bigDataCloudError) {
        console.error('BigDataCloud fallback failed:', bigDataCloudError);

        // FALLBACK 3: Last resort - try client-side reverse geocoding with browser
        try {
          console.log('Attempting IP-based geolocation fallback...');
          const response = await fetch(`https://ipapi.co/json/`);

          if (!response.ok) {
            throw new Error(`IP Geolocation API returned ${response.status}`);
          }

          const data = await response.json();

          if (data) {
            return {
              city: data.city || null,
              state: data.region || null,
              country: data.country_name || null,
              formatted: `${data.city || ''}, ${data.region || ''}, ${data.country_name || ''}`,
              latitude,
              longitude
            };
          }
          throw new Error('No results from IP Geolocation');
        } catch (ipGeoError) {
          console.error('All geocoding fallbacks failed:', ipGeoError);

          // Final fallback: Use approximate location based on coordinates
          return getApproximateLocationFromCoordinates(latitude, longitude);
        }
      }
    }
  }
};

// Helper function to get approximate location based on coordinates when all APIs fail
const getApproximateLocationFromCoordinates = (latitude: number, longitude: number): LocationInfo => {
  console.log('Using approximate location based on coordinates');

  // Function to get country and region based on rough coordinate mapping
  const getRegionInfo = (lat: number, lng: number): { country: string, state: string, city: string } => {
    // North America
    if (lat > 15 && lat < 72 && lng > -170 && lng < -50) {
      if (lng > -140 && lng < -50 && lat > 25 && lat < 50) {
        return { country: 'United States', state: 'Unknown State', city: 'Unknown City' };
      } else if (lat > 45 && lat < 72) {
        return { country: 'Canada', state: 'Unknown Province', city: 'Unknown City' };
      } else if (lat < 25 && lat > 15) {
        return { country: 'Mexico', state: 'Unknown State', city: 'Unknown City' };
      }
    }

    // Europe
    if (lat > 35 && lat < 70 && lng > -10 && lng < 40) {
      return { country: 'Europe', state: 'Unknown Region', city: 'Unknown City' };
    }

    // Asia
    if (lat > 10 && lat < 60 && lng > 60 && lng < 150) {
      return { country: 'Asia', state: 'Unknown Region', city: 'Unknown City' };
    }

    // Default for other regions
    return {
      country: 'Unknown Country',
      state: 'Unknown Region',
      city: 'Unknown City'
    };
  };

  const regionInfo = getRegionInfo(latitude, longitude);

  return {
    city: regionInfo.city,
    state: regionInfo.state,
    country: regionInfo.country,
    formatted: `${regionInfo.city}, ${regionInfo.state}, ${regionInfo.country} (Coordinates: ${latitude.toFixed(4)}, ${longitude.toFixed(4)})`,
    latitude,
    longitude
  };
};

// Utility to retry API calls with increasing timeouts
export const retryWithTimeout = async <T>(
  apiCall: () => Promise<T>,
  options: {
    maxAttempts?: number;
    initialTimeout?: number;
    timeoutMultiplier?: number;
    onRetry?: (attempt: number, error: any) => void;
  } = {}
): Promise<T> => {
  const {
    maxAttempts = 3,
    initialTimeout = 3000,
    timeoutMultiplier = 2,
    onRetry = () => { }
  } = options;

  let attempt = 0;
  let lastError: any = null;

  while (attempt < maxAttempts) {
    try {
      attempt++;
      return await apiCall();
    } catch (error: any) {
      lastError = error;

      // Check if we've reached max attempts
      if (attempt >= maxAttempts) {
        break;
      }

      // Calculate backoff time
      const timeout = initialTimeout * Math.pow(timeoutMultiplier, attempt - 1);

      // Call the retry callback
      onRetry(attempt, error);

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, timeout));
    }
  }

  throw lastError;
};

